import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Complaint, Contractor, ContractorAddress, PersonsService, State } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import Fuse from 'fuse.js';
import { ComplaintParty, ComplaintPartyExtended } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { ComplaintService } from '../complaint.service';
import { Subscription } from 'rxjs';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SyncService, getSelectedComplaint } from '@icc/b2b/sales-document';
import { Store } from '@ngrx/store';
import { logger } from '@icc/shared';
import { NgxPermissionsService } from 'ngx-permissions';

@Component({
    selector: 'icc-complaint-change-user-dialog',
    templateUrl: './complaint-change-user-dialog.component.html',
    styleUrls: ['./complaint-change-user-dialog.component.scss'],
})
export class ComplaintChangeUserDialogComponent implements OnInit, OnDestroy {

    /**
     * When true, the dialog is blocked because external changes were detected.
     */
    isBlocked = false;

    /**
     * Flag indicating that a permission-affecting change was detected externally.
     * When true, the next store update will trigger a blocking check.
     */
    private pendingBlockCheck = false;

    /**
     * Timestamp when the dialog was opened.
     */
    private dialogOpenedAt = 0;

    /**
     * Cached value of whether user has manufacturer permission.
     * Loaded once on init since permissions don't change during dialog lifetime.
     */
    private hasManufacturerPermission = false;

    recordsOnPage = 25;
    searchWord = '';
    searchNip = '';
    searchAddress = '';
    searchCombined = '';
    contractors: ComplaintPartyExtended[] = [];
    allContractors: ComplaintPartyExtended[] = [];
    selectedContractor: ComplaintPartyExtended | null = null;
    private _page = 1;
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }
    mobileView = false;

    private subscriptions: Subscription[] = [];

    constructor(
        private dialogRef: DialogRef,
        private personsService: PersonsService,
        private complaintService: ComplaintService,
        @Inject(DIALOG_DATA)
        public data: {
            user: ComplaintParty;
            userType: string;
            companyId: number;
            chooseContractorContractor: boolean;
            complaint: Complaint;
        },
        private breakpointObserver: BreakpointObserver,
        private syncService: SyncService,
        private store: Store<State>,
        private permissionsService: NgxPermissionsService,
        private cdr: ChangeDetectorRef,
    ) {
        this.loadContractors();
    }

    async loadContractors() {
        this.complaintService.loadContractors();
        await this.complaintService.contractorsLoaded;
        this.allContractors = this.complaintService.contractors[this.data.companyId] ?? [];
        this.contractors = this.allContractors;
        this.selectedContractor = this.contractors?.find((c) => c.userId === this.data.user?.userId) ?? null;
    }

    async ngOnInit(): Promise<void> {
        this.dialogOpenedAt = Date.now();

        // Cache manufacturer permission - doesn't change during dialog lifetime
        this.hasManufacturerPermission = await this.permissionsService.hasPermission('complaints-manage-manufacturer');

        this.setupExternalChangesDetection();

        this.subscriptions.push(
            this.breakpointObserver.observe('(max-width: 550px) or (max-height: 600px)').subscribe((result) => {
                this.mobileView = result.matches;

                if (this.mobileView) {
                    this.searchAddress = '';
                    this.searchNip = '';
                    this.searchWord = '';
                } else {
                    this.searchCombined = '';
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    /**
     * Sets up detection of external changes.
     * Blocks the dialog only if an external change causes loss of edit permission.
     */
    private setupExternalChangesDetection(): void {
        if (!this.data.complaint?.uuid) {
            return;
        }

        // Fields that affect edit permission
        const permissionFields = [
            'assignedTo',
            'distributorStatusId',
            'manufacturerStatusId',
            'authorizedRetailerStatusId',
        ];

        this.subscriptions.push(
            // Subscribe to external changes - set flag when permission-affecting changes detected
            this.syncService.externalChanges$.subscribe(({
                documentIds,
                documentFields,
                timestamp
            }) => {
                // Ignore changes that happened before dialog was opened
                if (timestamp <= this.dialogOpenedAt) {
                    return;
                }

                // Check if this complaint was modified
                if (!documentIds.has(this.data.complaint.uuid)) {
                    return;
                }

                // If permission-affecting field changed, check immediately
                if (permissionFields.some(field => documentFields.has(field))) {
                    logger.log('[ComplaintChangeUserDialog] Permission field changed externally');
                    this.pendingBlockCheck = true;
                    // Check immediately with current data
                    if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                        logger.log('[ComplaintChangeUserDialog] BLOCKING - external change removed permission');
                        this.isBlocked = true;
                        this.cdr.detectChanges();
                    }
                }
            }),

            // Subscribe to complaint document changes - update data and check blocking if needed
            this.store.select(getSelectedComplaint).subscribe((complaint) => {
                if (complaint && this.data.complaint && complaint.uuid === this.data.complaint.uuid) {
                    Object.assign(this.data.complaint, complaint);

                    // If we have a pending block check, perform it now with updated data
                    if (this.pendingBlockCheck && !this.isBlocked) {
                        if (!this.currentStatusAllowEdit() || !this.isUserAssigned()) {
                            logger.log('[ComplaintChangeUserDialog] BLOCKING - external change removed permission');
                            this.isBlocked = true;
                            this.cdr.detectChanges();
                        }
                    }
                }
            }),
        );
    }

    /**
     * Checks if the current status allows editing.
     */
    private currentStatusAllowEdit(): boolean {
        const userType = this.complaintService.getUserType();
        const statusId = this.complaintService.getDocumentStatusForUser(this.data.complaint, userType);

        if (statusId === 0 || isNaN(statusId)) {
            return true;
        }

        const status = this.complaintService.statuses.find((s) => s.id === statusId);
        return status?.allowEdit ?? false;
    }

    /**
     * Checks if the current user is the assigned party or has manufacturer permission.
     * Users with 'complaints-manage-manufacturer' permission bypass the assignment check.
     */
    private isUserAssigned(): boolean {
        if (this.hasManufacturerPermission) {
            return true;
        }
        const userType = this.complaintService.getUserType();
        return this.data.complaint.assignedTo?.startsWith(userType) ?? false;
    }

    async handleSearch() {
        if (this.searchWord || this.searchNip || this.searchAddress) {
            let contractors = this.allContractors ?? [];

            if (this.searchWord) {
                contractors = this.handleSearchWord(this.searchWord, contractors);
            }
            if (this.searchNip) {
                contractors = this.handleSearchNip(this.searchNip, contractors);
            }
            if (this.searchAddress) {
                contractors = this.handleSearchAddress(this.searchAddress, contractors);
            }

            this.contractors = contractors;
        } else {
            this.contractors = this.allContractors ?? [];
        }
    }

    handleSearchWord(searchWord: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['companyName', 'email', 'phoneNumber'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchWord);
        return result;
    }

    handleSearchNip(searchNip: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['companyVatNr', 'companyVatNrEu'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchNip);
        return result;
    }

    handleSearchAddress(searchAddress: string, contractors: ComplaintPartyExtended[]): ComplaintPartyExtended[] {
        const options: Fuse.FuseOptions<any> = {
            minMatchCharLength: 3,
            threshold: 0.3,
            keys: ['address'],
        };
        const fuse = new Fuse(contractors, options);
        const result = fuse.search(searchAddress);
        return result;
    }

    getShortName(contractor: ComplaintPartyExtended): string {
        if (contractor?.companyName) {
            return contractor.companyName;
        }
        return '';
    }

    getLocalizationTextFromAddress(address: ContractorAddress): string {
        let localization = '';
        if (address?.countryId) {
            localization = address.countryId;
        }
        if (address?.city) {
            if (localization) localization += ', ';
            localization += address.city;
        }
        if (address?.postalCode) {
            if (localization) localization += ', ';
            localization += address.postalCode;
        }
        return localization;
    }

    getContractorLocalization(contractor: ComplaintPartyExtended): string {
        return contractor.address;
    }

    getContractorEmailPhone(contractor: ComplaintPartyExtended): string {
        let emailPhone = '';
        if (contractor?.email) {
            emailPhone = contractor?.email;
        }
        if (contractor?.phoneNumber) {
            if (emailPhone) emailPhone += ', ';
            emailPhone += contractor.phoneNumber;
        }
        return emailPhone;
    }

    onChangePage(page: number) {
        this.page = page;
    }

    select(contractor: ComplaintPartyExtended) {
        const changed = contractor?.userId !== this.selectedContractor?.userId;
        this.selectedContractor = contractor;
        if (changed) {
            this.dialogRef.close(contractor);
        } else {
            this.dialogRef.close();
        }
    }

    close() {
        this.dialogRef.close();
    }
}
